// Pack-format comparison for inquiry-item packaging flexibility.
//
// A PackagingSpec is the packaging subset of a variant MINUS baseUnit. baseUnit
// is deliberately excluded: it is the denominator every volume and price is
// expressed in, so it stays strictly matched by attributesCompatible's existing
// check and is never relaxed by the packaging flag. That exclusion is why
// packagingSpecLabel takes baseUnit as a parameter — the caller supplies the
// request's own fixed unit.
//
// packagingCompatible is MIRRORED ON THE BACKEND at user-mngt-srv
// src/modules/restaurant-inquiry/helpers/packaging-compatible.helper.ts —
// keep the two in parity. Everything else here is display/dedupe and FE-only.

import type { PackagingSpec } from 'apis/restaurant-inquiries';
import { packagingSummary } from 'pages/inquiry-processor/components/family-workspace/packaging';

export type PackagingSource = PackagingSpec;

const text = (value?: string | null): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed.length ? trimmed.toLowerCase() : null;
};

const num = (value?: number | null): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

// Normalized copy: blanks and non-finite numbers collapse to null (= unknown).
export function packagingSpecOf(source?: PackagingSource | null): PackagingSpec {
  return {
    itemUnit: text(source?.itemUnit),
    netContent: num(source?.netContent),
    packUnit: text(source?.packUnit),
    packSize: num(source?.packSize),
  };
}

export function isEmptySpec(spec: PackagingSpec): boolean {
  const s = packagingSpecOf(spec);
  return s.itemUnit === null && s.netContent === null && s.packUnit === null && s.packSize === null;
}

// Unknown on either side never disqualifies — the same rule the string
// attributes follow in attributesCompatible.
const sameText = (a?: string | null, b?: string | null): boolean => {
  const left = text(a);
  const right = text(b);
  return left === null || right === null || left === right;
};

const sameNum = (a?: number | null, b?: number | null): boolean => {
  const left = num(a);
  const right = num(b);
  return left === null || right === null || left === right;
};

export function packagingCompatible(request: PackagingSpec, candidate: PackagingSpec): boolean {
  return (
    sameText(request.itemUnit, candidate.itemUnit) &&
    sameNum(request.netContent, candidate.netContent) &&
    sameText(request.packUnit, candidate.packUnit) &&
    sameNum(request.packSize, candidate.packSize)
  );
}

// Whitelist narrowing, and DELIBERATELY not packagingCompatible: this one is
// one-directional. A field the alternative specifies must equal the candidate's
// value exactly — a candidate that doesn't know that field does NOT satisfy it.
// A field the alternative leaves unset is a wildcard. Mirrors how the string
// attributes narrow via `alternatives.includes(productValue)`, which likewise
// demands an exact match against a value the candidate actually has.
//
// Using the symmetric packagingCompatible here would be a bug: an alternative
// of {packUnit:'box', packSize:6} would "match" a candidate knowing only
// {itemUnit:'bag', netContent:5}, since every field is unknown on one side or
// the other — defeating the narrowing entirely.
export function packagingSatisfiesAlternative(alternative: PackagingSpec, candidate: PackagingSpec): boolean {
  const alt = packagingSpecOf(alternative);
  const cand = packagingSpecOf(candidate);
  return (
    (alt.itemUnit === null || alt.itemUnit === cand.itemUnit) &&
    (alt.netContent === null || alt.netContent === cand.netContent) &&
    (alt.packUnit === null || alt.packUnit === cand.packUnit) &&
    (alt.packSize === null || alt.packSize === cand.packSize)
  );
}

// Identity for dedupe, React keys and order-insensitive change detection.
export function packagingSpecKey(spec: PackagingSpec): string {
  const s = packagingSpecOf(spec);
  return [s.itemUnit ?? '', s.netContent ?? '', s.packUnit ?? '', s.packSize ?? ''].join('|');
}

// The distinct pack formats a set of variants offers, empty specs dropped.
// Key-sorted so the wizard's checkbox order doesn't shuffle between renders.
export function distinctPackagingSpecs(sources: Array<PackagingSource | null | undefined>): PackagingSpec[] {
  const byKey = new Map<string, PackagingSpec>();
  sources.forEach((source) => {
    const spec = packagingSpecOf(source);
    if (isEmptySpec(spec)) return;
    const key = packagingSpecKey(spec);
    if (!byKey.has(key)) byKey.set(key, spec);
  });
  return Array.from(byKey.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([, spec]) => spec);
}

// "1 carton × 12 packs × 1 kg" — the shared display form, via the family
// workspace's summariser. baseUnit comes from the caller (see file header).
//
// Guards on isEmptySpec first: packagingSummary's own degrade rule renders a
// lone baseUnit ("kg") when every packaging field is blank, which is correct
// for a full variant label but wrong here — a PackagingSpec with nothing set
// means no pack format is known at all, so this must render '—' regardless
// of the baseUnit the caller supplies.
export function packagingSpecLabel(spec: PackagingSpec, baseUnit?: string | null): string {
  if (isEmptySpec(spec)) return '—';
  return packagingSummary({
    baseUnit: baseUnit ?? '',
    itemUnit: spec.itemUnit ?? '',
    netContent: spec.netContent != null ? String(spec.netContent) : '',
    packUnit: spec.packUnit ?? '',
    packSize: spec.packSize != null ? String(spec.packSize) : '',
  });
}
